import { Card } from "shared/components/Card";
import { Switch } from "shared/components/forms/Switch";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApartmentsComVariant, Unit } from "types/landlord/address";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { useSettingsController } from "views/settings-integration/controller";
import { PropertyKind } from "shared/types";
import { Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { INTEGRATION_LOGO } from "views/settings-integration/constants";

interface ListingSectionProps {
  propertyId: number;
  propertyKind: PropertyKind | null;
  unit: Unit;
}

interface SyndicationState {
  zillow: boolean;
  apartmentsCom: boolean;
  apartmentsComVariant: ApartmentsComVariant | null;
  zumper: boolean;
}

const readSyndicationState = (
  unit: Unit,
  defaultVariant: ApartmentsComVariant
): SyndicationState => ({
  zillow: Boolean(unit.feed_zillow_enabled),
  apartmentsCom: Boolean(unit.feed_apartments_com_enabled),
  apartmentsComVariant:
    unit.feed_apartments_com_variant ??
    (unit.feed_apartments_com_enabled ? defaultVariant : null),
  zumper: Boolean(unit.feed_zumper_enabled)
});

const CLEARED_SYNDICATION: SyndicationState = {
  zillow: false,
  apartmentsCom: false,
  apartmentsComVariant: null,
  zumper: false
};

const PROPERTY_KIND_TO_APARTMENTS_VARIANT: Record<
  string,
  ApartmentsComVariant
> = {
  [PropertyKind.SingleFamily]: "single_family",
  [PropertyKind.MultiFamily]: "multi_family",
  default: "single_family"
};

const apartmentsVariantForKind = (
  propertyKind: PropertyKind | null
): ApartmentsComVariant =>
  PROPERTY_KIND_TO_APARTMENTS_VARIANT[propertyKind ?? "default"];

type TopLevelSource = "listings" | "zillow" | "apartments" | "zumper";

const computeLastTopLevel = ({
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled
}: {
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
}): TopLevelSource => {
  if (zumperEnabled) return "zumper";
  if (apartmentsComEnabled) return "apartments";
  if (zillowEnabled) return "zillow";
  return "listings";
};

const syndicationEqual = (a: SyndicationState, b: SyndicationState) =>
  a.zillow === b.zillow &&
  a.apartmentsCom === b.apartmentsCom &&
  a.apartmentsComVariant === b.apartmentsComVariant &&
  a.zumper === b.zumper;

const REPLICA_RETRY_DELAY_MS = 200;
const REPLICA_MAX_RETRIES = 3;
const AUTOSAVE_DEBOUNCE_MS = 500;

interface MagicLinkSnapshot {
  linkIsActive: boolean;
  unitListed: boolean;
  syndication: SyndicationState;
}

interface MagicLinkPayload {
  active?: boolean;
  listed?: boolean;
  feed_zillow_enabled?: boolean;
  feed_apartments_com_enabled?: boolean;
  feed_apartments_com_variant?: ApartmentsComVariant | null;
  feed_zumper_enabled?: boolean;
}

const diffMagicLinkPayload = (
  previous: MagicLinkSnapshot,
  next: MagicLinkSnapshot
): MagicLinkPayload => {
  const payload: MagicLinkPayload = {};

  if (previous.linkIsActive !== next.linkIsActive)
    payload.active = next.linkIsActive;
  if (previous.unitListed !== next.unitListed) payload.listed = next.unitListed;
  if (previous.syndication.zillow !== next.syndication.zillow)
    payload.feed_zillow_enabled = next.syndication.zillow;
  if (previous.syndication.apartmentsCom !== next.syndication.apartmentsCom)
    payload.feed_apartments_com_enabled = next.syndication.apartmentsCom;

  const nextVariant = next.syndication.apartmentsCom
    ? next.syndication.apartmentsComVariant
    : null;
  const previousVariant = previous.syndication.apartmentsCom
    ? previous.syndication.apartmentsComVariant
    : null;
  if (previousVariant !== nextVariant)
    payload.feed_apartments_com_variant = nextVariant;

  if (previous.syndication.zumper !== next.syndication.zumper)
    payload.feed_zumper_enabled = next.syndication.zumper;

  return payload;
};

export const ListingSection = ({
  propertyId,
  propertyKind,
  unit
}: ListingSectionProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const { isEverySyncEnabled } = useSettingsController();
  const { partner } = useAuthContext();

  const defaultApartmentsComVariant = apartmentsVariantForKind(propertyKind);

  const [linkIsActive, setLinkIsActive] = useState<boolean>(
    Boolean(unit.magic_link?.active)
  );
  const [unitListed, setUnitListed] = useState<boolean>(Boolean(unit.listed));
  const [syndication, setSyndication] = useState<SyndicationState>(() =>
    readSyndicationState(unit, defaultApartmentsComVariant)
  );

  const committedRef = useRef({
    linkIsActive,
    unitListed,
    syndication
  });
  const awaitingRefetchRef = useRef(false);
  const replicaRetryCountRef = useRef(0);
  const replicaRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const invalidatePropertyDetail = useCallback(() => {
    propertyKind === PropertyKind.SingleFamily
      ? queryClient.invalidateQueries({
          queryKey: ["properties", String(propertyId)]
        })
      : queryClient.invalidateQueries({
          queryKey: ["unit", String(propertyId), String(unit.id)]
        });
  }, [propertyKind, propertyId, unit.id, queryClient]);

  const handleAwaitingRefetch = useCallback(
    (matched: boolean): "done" | "continue" => {
      if (matched) {
        awaitingRefetchRef.current = false;
        replicaRetryCountRef.current = 0;
        if (replicaRetryTimerRef.current) {
          clearTimeout(replicaRetryTimerRef.current);
          replicaRetryTimerRef.current = null;
        }
        return "done";
      }

      if (replicaRetryCountRef.current < REPLICA_MAX_RETRIES) {
        replicaRetryCountRef.current += 1;
        if (replicaRetryTimerRef.current)
          clearTimeout(replicaRetryTimerRef.current);
        replicaRetryTimerRef.current = setTimeout(
          invalidatePropertyDetail,
          REPLICA_RETRY_DELAY_MS * replicaRetryCountRef.current
        );
        return "done";
      }

      awaitingRefetchRef.current = false;
      replicaRetryCountRef.current = 0;
      return "continue";
    },
    [invalidatePropertyDetail]
  );

  useEffect(
    () => () => {
      if (replicaRetryTimerRef.current)
        clearTimeout(replicaRetryTimerRef.current);
    },
    []
  );

  const usesDependentToggles =
    propertyKind === null || propertyKind === PropertyKind.SingleFamily;

  const enableAllowApplying = () => {
    setLinkIsActive(true);
  };

  const disableAllowApplying = () => {
    if (usesDependentToggles && unitListed) {
      setLinkIsActive(false);
      setUnitListed(false);
      setSyndication(CLEARED_SYNDICATION);
    } else setLinkIsActive(false);
  };

  const enableShowOnPortfolio = () => {
    if (usesDependentToggles && !linkIsActive) {
      setUnitListed(true);
      setLinkIsActive(true);
    } else setUnitListed(true);
  };

  const disableShowOnPortfolio = () => {
    setUnitListed(false);
    setSyndication(CLEARED_SYNDICATION);
  };

  const { mutate: updateUnitSettings, isPending } = useMutation({
    mutationKey: ["MAGIC_LINK_UPDATE", unit.magic_link?.id],
    mutationFn: async () => {
      const snapshot = {
        linkIsActive,
        unitListed,
        syndication
      };

      if (!unit.magic_link?.id) {
        committedRef.current = snapshot;
        return;
      }

      const payload = diffMagicLinkPayload(committedRef.current, snapshot);
      if (Object.keys(payload).length === 0) {
        committedRef.current = snapshot;
        return;
      }

      await addressApi.updateMagicLink(unit.magic_link.id, payload);

      committedRef.current = snapshot;
    },
    onSuccess: () => {
      awaitingRefetchRef.current = true;
      replicaRetryCountRef.current = 0;
      if (replicaRetryTimerRef.current) {
        clearTimeout(replicaRetryTimerRef.current);
        replicaRetryTimerRef.current = null;
      }
      invalidatePropertyDetail();
    },
    onError: (error) => {
      const last = committedRef.current;
      setLinkIsActive(last.linkIsActive);
      setUnitListed(last.unitListed);
      setSyndication(last.syndication);
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  useEffect(() => {
    if (isPending) return;

    const dirty =
      linkIsActive !== committedRef.current.linkIsActive ||
      unitListed !== committedRef.current.unitListed ||
      !syndicationEqual(syndication, committedRef.current.syndication);

    if (!dirty) return;

    const timeoutId = setTimeout(updateUnitSettings, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [linkIsActive, unitListed, syndication, isPending]);

  useEffect(() => {
    if (isPending) return;

    const serverActive = Boolean(unit.magic_link?.active);
    const serverListed = Boolean(unit.listed);
    const serverSyndication = readSyndicationState(
      unit,
      defaultApartmentsComVariant
    );

    const serverMatchesCommitted =
      serverActive === committedRef.current.linkIsActive &&
      serverListed === committedRef.current.unitListed &&
      syndicationEqual(serverSyndication, committedRef.current.syndication);

    if (
      awaitingRefetchRef.current &&
      handleAwaitingRefetch(serverMatchesCommitted) === "done"
    )
      return;

    if (serverMatchesCommitted) return;

    const localMatchesCommitted =
      linkIsActive === committedRef.current.linkIsActive &&
      unitListed === committedRef.current.unitListed &&
      syndicationEqual(syndication, committedRef.current.syndication);

    if (!localMatchesCommitted) return;

    setLinkIsActive(serverActive);
    setUnitListed(serverListed);
    setSyndication(serverSyndication);
    committedRef.current = {
      linkIsActive: serverActive,
      unitListed: serverListed,
      syndication: serverSyndication
    };
  }, [
    unit.magic_link?.active,
    unit.listed,
    unit.feed_zillow_enabled,
    unit.feed_apartments_com_enabled,
    unit.feed_apartments_com_variant,
    unit.feed_zumper_enabled,
    isPending,
    handleAwaitingRefetch,
    defaultApartmentsComVariant
  ]);

  const subjectLabel =
    propertyKind === PropertyKind.SingleFamily
      ? dictionary.property
      : dictionary.unit;
  const listToggleLabel = `List ${subjectLabel} on`;
  const allowApplyingLabel =
    propertyKind === null
      ? "Allow applying"
      : `Allow applying for ${subjectLabel}`;

  const toggleZillow = () =>
    setSyndication((prev) => ({ ...prev, zillow: !prev.zillow }));
  const toggleZumper = () =>
    setSyndication((prev) => ({ ...prev, zumper: !prev.zumper }));

  const toggleApartmentsCom = () =>
    setSyndication((prev) => {
      if (prev.apartmentsCom)
        return { ...prev, apartmentsCom: false, apartmentsComVariant: null };
      return {
        ...prev,
        apartmentsCom: true,
        apartmentsComVariant:
          prev.apartmentsComVariant ?? defaultApartmentsComVariant
      };
    });
  const pickApartmentsVariant = (variant: ApartmentsComVariant) =>
    setSyndication((prev) => ({ ...prev, apartmentsComVariant: variant }));

  return (
    <Card>
      <h3 className="text-base font-bold py-6 px-8 border-b border-alpha-40">
        Listing
      </h3>
      <div className="space-y-6 py-6 px-8">
        {isEverySyncEnabled && unit.pms_id ? (
          <AllowOrDisallowApplications
            isActive={linkIsActive}
            isListed={unitListed}
            withListedInfo
          />
        ) : (
          <div className="relative">
            <Switch
              label={allowApplyingLabel}
              checked={linkIsActive}
              className="text-base font-medium text-content-1000"
              onChange={() => {
                if (linkIsActive) disableAllowApplying();
                else enableAllowApplying();
              }}
            />

            <span
              aria-hidden
              className="absolute border-l border-content-100 left-[22px] top-6 h-7"
            />
            <span
              aria-hidden
              className="absolute border-t border-content-100 left-[22px] top-[52px] w-[34px]"
            />

            <div className="ml-14 pt-4">
              <Switch
                label={listToggleLabel}
                checked={unitListed}
                className="text-base font-medium text-content-1000"
                onChange={() => {
                  if (unitListed) disableShowOnPortfolio();
                  else enableShowOnPortfolio();
                }}
              />
              <SyndicationSourcesPanel
                unitListed={unitListed}
                hasListing={Boolean(unit.has_listing)}
                syndication={syndication}
                zillowEnabled={Boolean(partner?.zillow_connected)}
                apartmentsComEnabled={Boolean(
                  partner?.apartments_com_connected
                )}
                zumperEnabled={Boolean(partner?.zumper_connected)}
                onToggleZillow={toggleZillow}
                onToggleApartmentsCom={toggleApartmentsCom}
                onPickApartmentsVariant={pickApartmentsVariant}
                onToggleZumper={toggleZumper}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

interface SyndicationSourcesPanelProps {
  unitListed: boolean;
  hasListing: boolean;
  syndication: SyndicationState;
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  onToggleZillow: () => void;
  onToggleApartmentsCom: () => void;
  onPickApartmentsVariant: (variant: ApartmentsComVariant) => void;
  onToggleZumper: () => void;
}

const SyndicationSourcesPanel = ({
  unitListed,
  hasListing,
  syndication,
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  onToggleZillow,
  onToggleApartmentsCom,
  onPickApartmentsVariant,
  onToggleZumper
}: SyndicationSourcesPanelProps) => {
  const sectionDisabled = !unitListed || !hasListing;

  const lastTopLevel = computeLastTopLevel({
    zillowEnabled,
    apartmentsComEnabled,
    zumperEnabled
  });

  return (
    <div className="mt-2 ml-6 relative pl-5">
      <div className="space-y-2">
        <TreeRow>
          <SourceCheckbox
            label="Portfolio page"
            icon={boomBrandMark}
            checked={unitListed}
            disabled
            onChange={() => undefined}
          />
        </TreeRow>
        <TreeRow isLast={lastTopLevel === "listings"}>
          <SourceCheckbox
            label="Listings page"
            icon={boomBrandMark}
            checked={unitListed}
            disabled
            onChange={() => undefined}
          />
        </TreeRow>
        <Show if={zillowEnabled}>
          <TreeRow isLast={lastTopLevel === "zillow"}>
            <SourceCheckbox
              label="Zillow"
              icon={<SourceLogo id={IntegrationId.ZILLOW} />}
              checked={unitListed && syndication.zillow}
              disabled={sectionDisabled}
              onChange={onToggleZillow}
            />
          </TreeRow>
        </Show>
        <Show if={apartmentsComEnabled}>
          <TreeRow isLast={lastTopLevel === "apartments"}>
            <SourceCheckbox
              label="Apartments.com"
              icon={<SourceLogo id={IntegrationId.APARTMENTS_COM} />}
              checked={unitListed && syndication.apartmentsCom}
              disabled={sectionDisabled}
              onChange={onToggleApartmentsCom}
            />
            <div className="mt-2 ml-3 relative pl-5">
              <div className="space-y-2">
                <TreeRow>
                  <SourceCheckbox
                    label="Single Family"
                    checked={
                      unitListed &&
                      syndication.apartmentsCom &&
                      syndication.apartmentsComVariant === "single_family"
                    }
                    disabled={sectionDisabled || !syndication.apartmentsCom}
                    onChange={() => onPickApartmentsVariant("single_family")}
                  />
                </TreeRow>
                <TreeRow isLast>
                  <SourceCheckbox
                    label="Multi Family"
                    checked={
                      unitListed &&
                      syndication.apartmentsCom &&
                      syndication.apartmentsComVariant === "multi_family"
                    }
                    disabled={sectionDisabled || !syndication.apartmentsCom}
                    onChange={() => onPickApartmentsVariant("multi_family")}
                  />
                </TreeRow>
              </div>
            </div>
          </TreeRow>
        </Show>
        <Show if={zumperEnabled}>
          <TreeRow isLast>
            <SourceCheckbox
              label="Zumper"
              icon={<SourceLogo id={IntegrationId.ZUMPER} />}
              checked={unitListed && syndication.zumper}
              disabled={sectionDisabled}
              onChange={onToggleZumper}
            />
          </TreeRow>
        </Show>
      </div>
    </div>
  );
};

const TreeRow = ({
  children,
  isLast = false
}: {
  children: React.ReactNode;
  isLast?: boolean;
}) => (
  <div className="relative">
    <span
      aria-hidden
      className={
        isLast
          ? "absolute left-[-20px] -top-2 h-[18px] border-l border-content-100"
          : "absolute left-[-20px] -top-2 -bottom-2 border-l border-content-100"
      }
    />
    <span
      aria-hidden
      className="absolute left-[-20px] top-[10px] w-5 border-t border-content-100"
    />
    {children}
  </div>
);

interface SourceCheckboxProps {
  label: string;
  icon?: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}

const SourceLogo = ({ id }: { id: IntegrationId }) => {
  const logo = INTEGRATION_LOGO[id];
  if (!logo) return null;

  return logo.Icon ? (
    <logo.Icon className="size-5 shrink-0 rounded-md object-contain" />
  ) : (
    <img
      src={logo.src}
      alt={id}
      className="size-5 shrink-0 rounded-md object-contain"
    />
  );
};

const boomBrandMark = (
  <img
    src="/assets/boom_brand_mark.svg"
    alt=""
    aria-hidden="true"
    className="size-5 shrink-0"
  />
);

const SourceCheckbox = ({
  label,
  icon,
  checked,
  disabled,
  onChange
}: SourceCheckboxProps) => (
  <label className="flex items-center gap-3 w-fit cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className={`h-5 w-5 shrink-0 rounded text-primary-500 focus:ring-0 focus:ring-offset-0 checked:disabled:bg-content-100 disabled:bg-background-900 disabled:cursor-not-allowed ${
        checked && !disabled ? "border-primary-500" : "border-content-100"
      }`}
    />
    {icon ? (
      <span className="h-5 w-5 shrink-0 inline-flex items-center justify-center [&>*]:!size-5 [&>img]:object-contain [&>svg]:size-5">
        {icon}
      </span>
    ) : null}
    <span
      className={`text-base font-medium leading-5 transition-all ${
        disabled ? "text-content-100" : "text-content-700"
      }`}
    >
      {label}
    </span>
  </label>
);
